import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * True North — vue React staircase + debrief (cyanotype Wave M2-game-chrome).
 *
 * MVP — path-2D top-down ; FPS POV motion comes Sprint 3+ (capacitor camera + AR boussole).
 */
import { useCallback, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DebriefShell, useGameShellFromContext } from '@labrain/game-shell';
import { staircaseConfig } from './params';
const STAGE = 280;
const CENTER = STAGE / 2;
/**
 * Reconstruit la liste des points du chemin (origine + extrémités segments).
 * L origine est centrée; on scale+translate pour rester dans le stage.
 */
function pathPoints(stim) {
    const pts = [{ x: 0, y: 0 }];
    let x = 0;
    let y = 0;
    for (const seg of stim.path) {
        const rad = (seg.bearing * Math.PI) / 180;
        x += Math.sin(rad) * seg.length;
        y += -Math.cos(rad) * seg.length;
        pts.push({ x, y });
    }
    return pts;
}
function computeStageMetrics(pts) {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const p of pts) {
        if (p.x < minX)
            minX = p.x;
        if (p.y < minY)
            minY = p.y;
        if (p.x > maxX)
            maxX = p.x;
        if (p.y > maxY)
            maxY = p.y;
    }
    const padding = 28;
    const w = Math.max(1, maxX - minX);
    const h = Math.max(1, maxY - minY);
    const scale = Math.min((STAGE - padding * 2) / w, (STAGE - padding * 2) / h);
    // Centrer la bounding box dans le stage.
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    const offsetX = CENTER - cx * scale;
    const offsetY = CENTER - cy * scale;
    return { scale, offsetX, offsetY };
}
function PathRenderer({ stim, walkerSegmentIdx, showFullPath, pickedBearing, trueBackBearing, }) {
    const pts = pathPoints(stim);
    const metrics = computeStageMetrics(pts);
    const proj = (p) => ({
        x: p.x * metrics.scale + metrics.offsetX,
        y: p.y * metrics.scale + metrics.offsetY,
    });
    const projected = pts.map(proj);
    const start = projected[0];
    const end = projected[projected.length - 1];
    // Walker = position fin du segment courant pendant path_show.
    const walkerIdx = Math.min(Math.max(0, walkerSegmentIdx), projected.length - 1);
    const walker = projected[walkerIdx] ?? start;
    // Polyline visible jusqu au segment courant.
    const visiblePts = showFullPath
        ? projected
        : projected.slice(0, walkerIdx + 1);
    const polyline = visiblePts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
    // Overlay flèches (feedback).
    const overlayLength = 70;
    const projectedFromEnd = (bearingDeg) => {
        const rad = (bearingDeg * Math.PI) / 180;
        return {
            x: end.x + Math.sin(rad) * overlayLength,
            y: end.y + -Math.cos(rad) * overlayLength,
        };
    };
    const pickedTip = pickedBearing !== undefined ? projectedFromEnd(pickedBearing) : null;
    const trueTip = trueBackBearing !== undefined ? projectedFromEnd(trueBackBearing) : null;
    return (_jsxs("svg", { width: STAGE, height: STAGE, viewBox: `0 0 ${STAGE} ${STAGE}`, className: "border border-cyan-border-faint bg-cyan-surface", "aria-hidden": true, children: [visiblePts.length >= 2 && (_jsx("polyline", { points: polyline, fill: "none", stroke: "#1a3a5c", strokeWidth: 3, strokeLinecap: "round", strokeLinejoin: "round", opacity: 0.9 })), _jsx("circle", { cx: start.x, cy: start.y, r: 6, fill: "#0a1628" }), _jsx("text", { x: start.x + 10, y: start.y + 4, fontSize: 10, fontFamily: "ui-monospace, monospace", fill: "#1a3a5c", children: "S" }), !showFullPath && walkerIdx > 0 && (_jsx("circle", { cx: walker.x, cy: walker.y, r: 5, fill: "#1a3a5c" })), showFullPath && (_jsx("circle", { cx: end.x, cy: end.y, r: 5, fill: "#1a3a5c" })), pickedTip && (_jsx("line", { x1: end.x, y1: end.y, x2: pickedTip.x, y2: pickedTip.y, stroke: "#3a78b5", strokeWidth: 3, strokeLinecap: "round" })), trueTip && (_jsx("line", { x1: end.x, y1: end.y, x2: trueTip.x, y2: trueTip.y, stroke: "#0a1628", strokeWidth: 3, strokeLinecap: "round", strokeDasharray: "4 3" }))] }));
}
function CompassDial({ onConfirm, initialBearing, disabled }) {
    const { t } = useTranslation('games');
    const [bearing, setBearing] = useState(initialBearing ?? 0);
    const dialRef = useRef(null);
    const SIZE = 240;
    const RADIUS = SIZE / 2;
    const computeBearing = useCallback((clientX, clientY) => {
        const node = dialRef.current;
        if (!node)
            return;
        const rect = node.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = clientX - cx;
        const dy = clientY - cy;
        let deg = (Math.atan2(dx, -dy) * 180) / Math.PI;
        if (deg < 0)
            deg += 360;
        setBearing(deg);
    }, []);
    const handlePointerMove = (e) => {
        if (disabled)
            return;
        if (e.buttons === 0 && e.pointerType === 'mouse')
            return;
        computeBearing(e.clientX, e.clientY);
    };
    const handlePointerDown = (e) => {
        if (disabled)
            return;
        e.target.setPointerCapture?.(e.pointerId);
        computeBearing(e.clientX, e.clientY);
    };
    // Style pointeur : ligne du centre au bord, orientée selon bearing.
    const tipX = RADIUS + Math.sin((bearing * Math.PI) / 180) * (RADIUS - 22);
    const tipY = RADIUS + -Math.cos((bearing * Math.PI) / 180) * (RADIUS - 22);
    return (_jsxs("div", { className: "flex flex-col items-center gap-s3", children: [_jsxs("div", { ref: dialRef, onPointerDown: handlePointerDown, onPointerMove: handlePointerMove, className: "relative touch-none rounded-full border border-cyan-border bg-cyan-surface", style: { width: SIZE, height: SIZE }, children: [['n', 'e', 's', 'w'].map((dir, i) => {
                        const angle = i * 90;
                        const x = RADIUS + Math.sin((angle * Math.PI) / 180) * (RADIUS - 12);
                        const y = RADIUS + -Math.cos((angle * Math.PI) / 180) * (RADIUS - 12);
                        return (_jsx("span", { className: "pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 font-mono text-[12px] uppercase text-cyan-text", style: { left: x, top: y }, children: t(`true_north.buttons.${dir}_label`) }, dir));
                    }), _jsxs("svg", { width: SIZE, height: SIZE, viewBox: `0 0 ${SIZE} ${SIZE}`, className: "pointer-events-none absolute inset-0", children: [_jsx("line", { x1: RADIUS, y1: RADIUS, x2: tipX, y2: tipY, stroke: "#0a1628", strokeWidth: 4, strokeLinecap: "round" }), _jsx("circle", { cx: RADIUS, cy: RADIUS, r: 6, fill: "#1a3a5c" }), _jsx("circle", { cx: tipX, cy: tipY, r: 5, fill: "#0a1628" })] })] }), _jsxs("p", { className: "font-mono text-[12px] text-cyan-text-secondary", children: [Math.round(bearing), "\u00B0"] }), _jsx("button", { type: "button", disabled: disabled, onClick: () => onConfirm(bearing), className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network disabled:cursor-not-allowed disabled:opacity-50", children: t('true_north.buttons.confirm') })] }));
}
function FixationCross() {
    return (_jsx("div", { className: "flex items-center justify-center border border-cyan-border-faint bg-cyan-surface", style: { width: STAGE, height: STAGE }, children: _jsx("span", { className: "select-none font-display text-[44px] text-cyan-text", children: "+" }) }));
}
function HiddenStage() {
    return (_jsx("div", { className: "flex items-center justify-center border border-cyan-border-faint bg-cyan-surface", style: { width: STAGE, height: STAGE }, "aria-hidden": true }));
}
export function TNStaircaseView({ adapter, level: _level, onAbort }) {
    const { t } = useTranslation(['games', 'common']);
    const subscribe = useCallback((notify) => adapter.subscribe(() => notify()), [adapter]);
    const getSnapshot = useCallback(() => adapter.getState(), [adapter]);
    const adapterState = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    const shell = useGameShellFromContext();
    const handleConfirm = useCallback((bearing) => adapter.handlePoint(bearing), [adapter]);
    const trialDisplay = shell.snapshot.staircase.trialIdx + 1;
    const stim = adapterState.stim;
    const lastResp = adapterState.lastResponse;
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4", children: [_jsxs("div", { className: "flex w-full items-center justify-between text-[12px] text-cyan-text-secondary", children: [_jsx("span", { className: "font-mono uppercase tracking-[0.20em] text-cyan-text-faint", children: t('common:labels.trial_count', {
                            current: Math.min(trialDisplay, staircaseConfig.maxTrials),
                            total: staircaseConfig.maxTrials,
                        }) }), _jsx("button", { type: "button", onClick: () => {
                            shell.abort('user');
                            onAbort();
                        }, className: "border border-cyan-border-faint px-s3 py-s1 text-[11px] text-cyan-text-secondary hover:border-cyan-border", children: t('common:actions.stop') })] }), _jsxs("div", { className: "flex h-[24px] items-center justify-center text-[13px] text-cyan-text-secondary", children: [adapterState.subPhase === 'awaiting_pointer' && (_jsx("span", { children: t('true_north.question') })), adapterState.subPhase === 'feedback' && lastResp && (_jsx("span", { className: [
                            'font-display',
                            adapterState.feedback === 'correct' ? 'text-cyan-core' : 'text-cyan-media',
                        ].join(' '), children: adapterState.feedback === 'correct'
                            ? `✓ ${t('true_north.feedback.correct_with_error', {
                                error: Math.round(lastResp.errorDeg),
                            })}`
                            : `✗ ${t('true_north.feedback.incorrect')}` }))] }), _jsxs("div", { className: "relative", children: [adapterState.subPhase === 'fixation' && _jsx(FixationCross, {}), adapterState.subPhase === 'path_show' && stim && (_jsx(PathRenderer, { stim: stim, walkerSegmentIdx: adapterState.walkerSegmentIdx, showFullPath: false })), adapterState.subPhase === 'awaiting_pointer' && (_jsx("div", { className: "flex items-center justify-center border border-cyan-border-faint bg-cyan-surface", style: { width: STAGE, height: STAGE }, children: _jsx(CompassDial, { onConfirm: handleConfirm, disabled: false }) })), adapterState.subPhase === 'feedback' && stim && lastResp && (_jsx(PathRenderer, { stim: stim, walkerSegmentIdx: stim.path.length - 1, showFullPath: true, pickedBearing: lastResp.pickedBearing, trueBackBearing: lastResp.trueBackBearing })), (adapterState.subPhase === 'iti' || adapterState.subPhase === 'idle') && _jsx(HiddenStage, {})] }), _jsxs("div", { className: "text-[11px] text-cyan-text-secondary", children: [_jsx("span", { className: "font-mono", children: stim?.nTurns ?? '—' }), " ", t('true_north.turns_label'), ' · ', t('true_north.tolerance_label'), ' ', _jsxs("span", { className: "font-mono", children: ["\u00B1", adapterState.variant.acceptanceDeg, "\u00B0"] })] })] }));
}
function recommendKey(threshold) {
    if (threshold !== null && threshold >= 5) {
        return 'true_north.debrief.recommend_excellent';
    }
    if (threshold !== null && threshold >= 3) {
        return 'true_north.debrief.recommend_good';
    }
    return 'true_north.debrief.recommend_keep';
}
export function TNDebriefView({ level: _level, onAbort: _onAbort }) {
    const { t } = useTranslation(['games', 'common']);
    const shell = useGameShellFromContext();
    const staircase = shell.snapshot.staircase;
    const threshold = staircase.thresholdEstimate;
    const trialsCompleted = staircase.trialIdx;
    const trialsCorrect = staircase.trialLog.filter((tt) => tt.response.correct).length;
    const accuracy = trialsCompleted === 0
        ? 0
        : Math.round((trialsCorrect / trialsCompleted) * 100);
    const bestTurns = useMemo(() => {
        let best = 0;
        for (const tt of staircase.trialLog) {
            if (tt.response.correct && tt.value > best)
                best = tt.value;
        }
        return Math.round(best);
    }, [staircase]);
    const fallback = !!staircase.fallback;
    const thresholdLabel = threshold === null ? '—' : threshold.toFixed(1).replace(/\.0$/, '');
    const replay = () => window.location.reload();
    return (_jsx(DebriefShell, { eyebrow: t('common:eyebrow.session_done'), title: t('true_north.debrief.title'), scoreLabel: t('true_north.debrief.score_label'), scoreValue: thresholdLabel, equivalentAge: t('true_north.accuracy_global', { accuracy }), fallbackBadge: fallback, metrics: [
            { label: t('common:metrics.trials'), value: trialsCompleted },
            { label: t('common:metrics.correct_pct'), value: `${accuracy}%` },
            { label: t('true_north.debrief.metric_best_turns'), value: bestTurns },
        ], recommendation: t(recommendKey(threshold)), scientificCaveat: t('true_north.debrief.scientific_caveat'), ctaReplay: _jsx("button", { type: "button", onClick: replay, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: t('common:actions.replay') }), ctaBack: _jsx(Link, { to: "/library", className: "inline-flex h-12 w-full items-center justify-center border border-cyan-border bg-cyan-surface font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-text transition active:scale-[0.985] hover:bg-cyan-bg", children: t('common:actions.choose_another') }) }));
}
//# sourceMappingURL=staircaseView.js.map